// DRBFM (Design Review Based on Failure Mode) type definitions

// ==============================================================================
// Enums
// ==============================================================================

export type DRBFMStatus = 'draft' | 'analyzing' | 'completed' | 'failed';

export type Severity = 'critical' | 'high' | 'medium' | 'low';

export type CountermeasureType = 'prevention' | 'detection' | 'mitigation';

export type DesignChangeCategory =
  | 'Assembly'
  | 'Boundary'
  | 'Material'
  | 'Dimension'
  | 'Process'
  | 'Structure'
  | 'Surface'
  | 'Tolerance'
  | 'Function'
  | 'Other';

export type ImpactArea =
  | 'Assembly'
  | 'Appearance'
  | 'Design'
  | 'Durability'
  | 'Manufacturing'
  | 'Quality'
  | 'Safety'
  | 'Cost'
  | 'Maintenance';

export type DRBFMCaseStatus =
  | 'draft'
  | 'changes_extracted'
  | 'precheck_ready'
  | 'analyzing'
  | 'completed'
  | 'failed';

export type PrecheckAnswerStatus =
  | 'unanswered'
  | 'answered'
  | 'unknown'
  | 'not_applicable';

export type DRBFMCaseWorkflowStep =
  | 'case-intake'
  | 'change-review'
  | 'precheck'
  | 'analysis'
  | 'results';

// ==============================================================================
// Domain Models
// ==============================================================================

export interface FlowProgress {
  flow1?: 'pending' | 'in_progress' | 'completed' | 'failed';
  flow2?: 'pending' | 'in_progress' | 'completed' | 'failed';
  flow3?: 'pending' | 'in_progress' | 'completed' | 'failed';
  flow4?: 'pending' | 'in_progress' | 'completed' | 'failed';
  flow5?: 'pending' | 'in_progress' | 'completed' | 'failed';
}

export interface DRBFMSession {
  id: string;
  tenantId: string;
  userId: string;
  itemName: string;
  title?: string;
  specOldDocumentId?: string;
  specNewDocumentId?: string;
  status: DRBFMStatus;
  currentFlow: number;
  flowProgress: FlowProgress;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  htmlExportPath?: string;
}

export interface DesignChange {
  id: string;
  sessionId?: string;
  category: DesignChangeCategory;
  itemName: string;
  oldSpecification?: string;
  newSpecification?: string;
  changeDescription?: string;
  impactAreas: ImpactArea[];
  createdAt?: string;
}

export interface DRBFMCaseInputData {
  title: string;
  productName: string;
  targetPart: string;
  changeSummary: string;
  sourceDocumentIds?: string[];
  context?: Record<string, unknown>;
}

export interface DRBFMCaseDesignChange {
  id: string;
  caseId?: string;
  category: DesignChangeCategory;
  itemName: string;
  oldSpecification: string;
  newSpecification: string;
  changeDescription: string;
  impactAreas: ImpactArea[];
  confidence?: number | null;
  source: Record<string, unknown>;
  sortOrder: number;
  createdAt?: string;
}

export interface DRBFMCasePrecheckItem {
  id: string;
  caseId?: string;
  question: string;
  reason: string;
  requiredContextKey: string;
  answerStatus: PrecheckAnswerStatus;
  answerText?: string | null;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface DRBFMCase {
  id: string;
  tenantId?: string;
  userId?: string;
  linkedSessionId?: string | null;
  title: string;
  productName: string;
  targetPart: string;
  changeSummary: string;
  sourceDocumentIds: string[];
  context: Record<string, unknown>;
  status: DRBFMCaseStatus;
  designChanges: DRBFMCaseDesignChange[];
  precheckItems: DRBFMCasePrecheckItem[];
  createdAt?: string;
  updatedAt?: string;
  completedAt?: string | null;
}

export interface UsedPrecheckAnswer {
  id?: string;
  required_context_key?: string;
  requiredContextKey?: string;
  question?: string;
  answer?: string;
  answer_text?: string;
  answerText?: string;
  status?: PrecheckAnswerStatus | string;
}

export interface KnowledgeReference {
  documentId: string;
  title: string;
  fileName?: string;
  snippet?: string;
  relevanceScore: number;
  category?: string;
  pageNumber?: number;
  imageId?: string;
  metadata?: Record<string, unknown>;
}

export interface EvidenceBBox {
  x: number;
  y: number;
  width: number;
  height: number;
  unit: 'normalized' | 'pixel' | 'pdf_point';
}

export interface DrawingEvidence {
  documentId: string;
  imageId?: string;
  pageNumber?: number;
  label: string;
  confidence: number;
  source: 'document_image' | 'pdf_page' | 'llm_inferred';
  bbox: EvidenceBBox;
  reasoning?: string;
}

export interface ReviewQuestion {
  id: string;
  question: string;
  reason?: string;
  required: boolean;
}

// CAD anchor attached to a failure mode by the DRBFM↔CAD linker (TG #673)
export interface CadAnchor {
  cadModelId: string;
  partLabel: string;
  position: [number, number, number];
  confidence: number;
  source: 'pre_label' | 'hotspot' | 'llm_inferred';
  reasoning?: string;
}

export interface PredefinedPartLabel {
  label: string;
  position: [number, number, number];
  category: string;
  note?: string | null;
}

export interface FailureMode {
  id: string;
  sessionId?: string;
  designChangeId?: string;
  failureDescription: string;
  failureCause?: string;
  failureEffect?: string;
  severity: Severity;
  severityScore: number;
  rpnScore?: number;
  knowledgeReferences: KnowledgeReference[];
  cadAnchor?: CadAnchor;
  drawingEvidence?: DrawingEvidence;
  reviewQuestions?: ReviewQuestion[];
  usedPrecheckAnswers?: UsedPrecheckAnswer[];
  unknownAssumptions?: string[];
  createdAt?: string;
}

export interface Countermeasure {
  id: string;
  failureModeId?: string;
  sessionId?: string;
  countermeasureType: CountermeasureType;
  description: string;
  effectivenessScore: number;
  priority: number;
  createdAt?: string;
}

// ==============================================================================
// API Request/Response Types
// ==============================================================================

export interface CreateSessionRequest {
  itemName: string;
  title?: string;
  specOldDocumentId?: string;
  specNewDocumentId?: string;
}

export interface CreateSessionResponse {
  id: string;
  itemName: string;
  title?: string;
  status: DRBFMStatus;
  currentFlow: number;
  createdAt: string;
}

export interface SessionListResponse {
  sessions: DRBFMSession[];
  total: number;
  limit: number;
  offset: number;
}

export interface AnalyzeRequest {
  oldSpecification: string;
  newSpecification: string;
  cadModelId?: string;
  predefinedPartLabels?: PredefinedPartLabel[];
}

export interface SessionResultsResponse {
  session: DRBFMSession;
  designChanges: DesignChange[];
  failureModes: FailureMode[];
  countermeasures: Countermeasure[];
}

export interface DRBFMCaseAnalyzeResult extends SessionResultsResponse {
  case: DRBFMCase;
}

export interface ConvertToVerificationRequest {
  failureModeIds: string[];
  verificationSessionId?: string | null;
  verificationUnitId?: string | null;
  newSessionTitle?: string | null;
  newSessionModelName?: string | null;
  newUnitNumber?: string | null;
  newUnitName?: string | null;
}

export interface ConvertToVerificationResponse {
  verificationSessionId: string;
  verificationUnitId: string;
  itemsCreated: number;
  items: Record<string, unknown>[];
}

// ==============================================================================
// SSE Event Types
// ==============================================================================

export type DRBFMEventType =
  | 'connected'
  | 'flow_start'
  | 'flow_progress'
  | 'flow_complete'
  | 'cad_link_complete'
  | 'cad_link_failed'
  | 'complete'
  | 'error';

export interface DRBFMProgressEvent {
  type: DRBFMEventType;
  flow: number;
  message: string;
  progress: number; // 0.0 to 1.0
  data?: {
    designChangesCount?: number;
    knowledgeRefsCount?: number;
    failureModesCount?: number;
    criticalCount?: number;
    countermeasuresCount?: number;
    exportPath?: string;
    linkedCount?: number;
    unresolvedFailureModeIds?: string[];
    errorCode?: string;
  };
  timestamp: string;
}

// ==============================================================================
// UI State Types
// ==============================================================================

export type DRBFMWizardStep = 'input' | 'processing' | 'results';

export interface DRBFMInputData {
  itemName: string;
  title?: string;
  oldSpecification: string;
  newSpecification: string;
  specOldDocumentId?: string;
  specNewDocumentId?: string;
  cadModelId?: string;
  predefinedPartLabels?: PredefinedPartLabel[];
}

export interface DRBFMAnalysisProgress {
  isRunning: boolean;
  currentFlow: number;
  overallProgress: number;
  flowStatuses: FlowProgress;
  messages: string[];
  error?: string;
}

export interface DRBFMState {
  // Session management
  sessions: DRBFMSession[];
  currentSession: DRBFMSession | null;
  sessionsLoading: boolean;
  sessionsError: string | null;

  // Analysis state
  analysisProgress: DRBFMAnalysisProgress;
  analysisResults: SessionResultsResponse | null;

  // UI state
  wizardStep: DRBFMWizardStep;
  inputData: DRBFMInputData;

  // Export state
  isExporting: boolean;
  exportError: string | null;
}

// ==============================================================================
// Helper Types
// ==============================================================================

export interface GroupedResults {
  designChange: DesignChange;
  failureModes: {
    failureMode: FailureMode;
    countermeasures: Countermeasure[];
  }[];
}

export interface DRBFMSummary {
  designChangesCount: number;
  failureModesCount: number;
  criticalCount: number;
  highCount: number;
  countermeasuresCount: number;
  preventionCount: number;
  detectionCount: number;
  mitigationCount: number;
}

// ==============================================================================
// Utility Functions (Type Guards)
// ==============================================================================

export function isSeverityCritical(severity: Severity): boolean {
  return severity === 'critical';
}

export function isSeverityHigh(severity: Severity): boolean {
  return severity === 'critical' || severity === 'high';
}

export function getSeverityColor(severity: Severity): string {
  switch (severity) {
    case 'critical':
      return '#d32f2f'; // red
    case 'high':
      return '#f57c00'; // orange
    case 'medium':
      return '#fbc02d'; // yellow
    case 'low':
      return '#388e3c'; // green
    default:
      return '#757575'; // grey
  }
}

export function getCountermeasureTypeColor(type: CountermeasureType): string {
  switch (type) {
    case 'prevention':
      return '#2e7d32'; // green
    case 'detection':
      return '#1565c0'; // blue
    case 'mitigation':
      return '#e65100'; // orange
    default:
      return '#757575'; // grey
  }
}

export function getCountermeasureTypeLabel(type: CountermeasureType): string {
  switch (type) {
    case 'prevention':
      return '予防';
    case 'detection':
      return '検出';
    case 'mitigation':
      return '軽減';
    default:
      return type;
  }
}

export function getCategoryColor(category: DesignChangeCategory): string {
  switch (category) {
    case 'Material':
      return '#1565c0';
    case 'Dimension':
      return '#c2185b';
    case 'Process':
      return '#2e7d32';
    case 'Structure':
      return '#e65100';
    case 'Function':
      return '#7b1fa2';
    default:
      return '#546e7a';
  }
}
